import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import usePerformanceInsightsApi, { SUGGESTED_ACTION_TYPE } from '../../performance-insights/config';
import InsightDetailDrawer from '../../performance-insights/InsightDetailDrawer';
import ActionFormDrawer from '../../performance-actions/ActionFormDrawer';
import api from '../../../services/api';
import { parseTableParams } from '../../../utils/Utils';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import { FINDINGS_ENDPOINT, OPEN_STATUSES, toFindingParams } from '../config';

// P7a — E15 orta sol: bulgu kuyruğu (başlık, önem, veri dönemi, n, kural, tespit/yenilenme, durum). Varsayılan açık bulgular
// (yeni + inceleniyor); süzgeç: tür, önem, durum, kapsam, tespit tarihi; dönem ve sorumlu sayfa şeridinden. Satır → detay çekmecesi
// (gözlem, hesaplama, kaynak, sınırlama, olası açıklama, önerilen aksiyon) → aksiyon taslağı (önerilen tür) → kullanıcı onayı
// (kaydet) → aksiyon. Aynı bulgu + tür için ikinci tıklama ikinci görev açmaz (sunucu mevcut aksiyonu döner).
// Bulgu tablosu, detay ve aksiyon çekmeceleri Bulgular/Aksiyonlar sayfalarının bileşenleridir (yeniden kullanım).
export default function FindingsTab({ periodId, ownerEmployeeId, isHr, containerHeight, onChanged }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const scope = useMemo(() => ({
    ...(periodId ? { 'periodId[eq]': periodId } : {}),
    ...(ownerEmployeeId ? { 'ownerEmployeeId[eq]': ownerEmployeeId } : {}),
  }), [periodId, ownerEmployeeId]);
  const [queryParams, setQueryParams] = useState(() => ({ pageSize: 10, pageNumber: 1, 'status[in]': OPEN_STATUSES, ...scope }));
  const [filterList, setFilterList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [actionFor, setActionFor] = useState(null);
  const [invalidFor, setInvalidFor] = useState(null);

  // Dönem/sorumlu değişince ilk sayfaya dönülür; tablo süzgeçleri korunur (ilk çizimde başlangıç durumu zaten kapsamlıdır).
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    setQueryParams((q) => toFindingParams({ ...q, pageNumber: 1, 'periodId[eq]': undefined, 'ownerEmployeeId[eq]': undefined }, scope));
  }, [scope]);

  const {
    fetching, data, pagination, columns: hookColumns, changeStatus, refresh,
  } = usePerformanceInsightsApi(queryParams, { endpoint: FINDINGS_ENDPOINT });

  // Tespit tarihi süzgeci (aralık) — kanca kolonunun yerine tarih süzgeçli kolon.
  const baseColumns = useMemo(() => hookColumns.map((c) => (c.name === 'detectedAt'
    ? { ...dateFilterColumn('detectedAt', t('performance.insight.detectedAt'), lang, { sort: false }) }
    : c)), [hookColumns, t, lang]);

  const handleTableChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    if (!params['status[in]']) params['status[in]'] = OPEN_STATUSES;
    setQueryParams(toFindingParams(params, scope));
  }, [baseColumns, scope]);

  const handleChangeStatus = useCallback(async (id, status, reason) => {
    const ok = await changeStatus(id, status, reason);
    if (ok) { setDetail(null); onChanged?.(); }
    return ok;
  }, [changeStatus, onChanged]);

  const handleCreateAction = useCallback(async (payload) => {
    try {
      await api.post('/v1/performance-actions', payload);
      refresh();
      setDetail(null);
      onChanged?.();
      return true;
    } catch {
      return false;
    }
  }, [refresh, onChanged]);

  const buildRowActions = useCallback((row) => {
    const live = ['new', 'reviewing', 'actioned'].includes(row.status);
    return [
      { type: 'details', label: t('performance.insight.actions.detail'), onClick: () => setDetail(row) },
      ...(row.status === 'new' ? [{ type: 'open', label: t('performance.insight.actions.review'), onClick: () => handleChangeStatus(row.id, 'reviewing') }] : []),
      ...(live ? [{ type: 'submit_for_approval', label: t('performance.insight.actions.openAction'), onClick: () => setActionFor(row) }] : []),
      ...(row.status === 'actioned' ? [{ type: 'close', label: t('performance.insight.actions.close'), onClick: () => handleChangeStatus(row.id, 'closed') }] : []),
      ...(row.status !== 'invalid' ? [{ type: 'reject', label: t('performance.insight.actions.invalidate'), onClick: () => setInvalidFor(row) }] : []),
    ];
  }, [t, handleChangeStatus]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row ? <OptionsMenu actions={buildRowActions(row)} row={row} /> : null;
        },
      },
    },
  ], [baseColumns, data, buildRowActions]);

  // Yönetici aksiyonu kendi ekibine açar: varsayılan sorumlu boş bırakılır, kapsam kişi seçimiyle sunucuda denetlenir.
  const actionDefaults = useMemo(() => (actionFor ? {
    sourceInsightId: actionFor.id,
    actionType: SUGGESTED_ACTION_TYPE[actionFor.ruleCode] || 'custom',
    title: t(`performance.insight.${actionFor.ruleCode}.title`, { defaultValue: actionFor.ruleCode }),
    ...(actionFor.scopeType === 'manager' ? { ownerEmployeeId: actionFor.scopeId } : {}),
  } : undefined), [actionFor, t]);

  const invalidSchema = useMemo(() => [
    { name: 'reason', label: t('performance.insight.invalidReason'), type: 'textarea', required: true, maxLength: 500, rows: 4 },
  ], [t]);

  return (
    <div style={{ height: '100%', minHeight: 0, overflow: 'hidden' }} data-tab="findings">
      <InsightDetailDrawer
        insight={detail}
        open={!!detail}
        isHr={isHr}
        onClose={() => setDetail(null)}
        onChangeStatus={handleChangeStatus}
        onOpenAction={(row) => setActionFor(row)}
        onInvalidate={(row) => setInvalidFor(row)}
      />
      <ActionFormDrawer open={!!actionFor} defaults={actionDefaults} onClose={() => setActionFor(null)} onSave={handleCreateAction} />
      <GenericFormDrawer
        open={!!invalidFor}
        onClose={() => setInvalidFor(null)}
        title={t('performance.insight.invalidTitle')}
        schema={invalidSchema}
        submitLabel={t('performance.insight.actions.invalidate')}
        onSave={async (form) => handleChangeStatus(invalidFor.id, 'invalid', form.reason)}
      />
      <DataTable
        title={t('performance.aiCenter.findingsTitle')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={setFilterList}
        onTableChanged={handleTableChange}
        canCreateNewRecord={false}
        tableBodyMaxHeight={containerHeight ? `${Math.max(containerHeight - 144, 200)}px` : undefined}
      />
    </div>
  );
}
